/**
 * LanguageSwitcher — compact EN | AR toggle used in the site header and
 * footer. Preserves the current pathname when switching (e.g. /track/xyz
 * → /ar/track/xyz). Does not use country flags; the active language is
 * highlighted in the DJ SPY brand red.
 */
import { useTranslation } from "react-i18next";
import { useLocale } from "@/lib/LocaleContext";

export default function LanguageSwitcher({ variant = "header", className = "" }) {
  const { lang, arEnabled, setLang } = useLocale();
  const { t } = useTranslation();
  if (!arEnabled) return null;

  const baseBtn =
    "font-mono tracking-[0.15em] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-red-brand rounded-full px-2.5 py-1 text-[10px]";
  const active = "text-red-brand";
  const inactive = "text-white/60 hover:text-white";
  const sep = variant === "footer" ? "text-white/25" : "text-white/25";

  return (
    <div
      role="group"
      aria-label={t("language.switch")}
      data-testid={`language-switcher-${variant}`}
      className={`inline-flex items-center gap-0.5 border border-white/10 rounded-full px-1 py-0.5 bg-black/30 ${className}`}
    >
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        aria-current={lang === "en" ? "true" : "false"}
        data-testid={`lang-btn-en-${variant}`}
        lang="en"
        dir="ltr"
        className={`${baseBtn} ${lang === "en" ? active : inactive}`}
      >
        English
      </button>
      <span aria-hidden="true" className={sep}>|</span>
      <button
        type="button"
        onClick={() => setLang("ar")}
        aria-pressed={lang === "ar"}
        aria-current={lang === "ar" ? "true" : "false"}
        data-testid={`lang-btn-ar-${variant}`}
        lang="ar"
        dir="rtl"
        style={{ fontFamily: '"IBM Plex Sans Arabic", sans-serif', letterSpacing: 0, fontWeight: 500 }}
        className={`${baseBtn} ${lang === "ar" ? active : inactive}`}
      >
        العربية
      </button>
    </div>
  );
}
